import { useCallback, useEffect, useRef, useState } from 'react';
import { useUserData } from '@/lib/auth';
import { findCandidatesForChat } from '@/lib/services/whatsapp/chatNumberCandidates';
import { searchClientsForChat, type ClientChatCandidate } from '@/lib/services/whatsapp/clientPhoneSearch';
import type { ChatNumberCandidate } from '@/lib/services/whatsapp/chatNumberMatch';

/**
 * Schede con lo stesso numero della chat (tutte, non solo la prima), lette solo
 * quando la chat non e' ancora collegata. Rispetta la visibilita' del ruolo.
 */
export function useChatNumberCandidates(chatJid: string, organizationId: string, enabled: boolean) {
  const { userProfile } = useUserData();
  const userId = userProfile?.id ?? null;
  const [state, setState] = useState<{ jid: string; list: ChatNumberCandidate[] } | null>(null);

  useEffect(() => {
    if (!enabled || !organizationId) return;
    let cancelled = false;
    findCandidatesForChat(organizationId, userId, chatJid)
      .then((list) => { if (!cancelled) setState({ jid: chatJid, list }); })
      .catch((err) => {
        console.error('[ClientBadge] ricerca schede per numero non riuscita:', err);
        if (!cancelled) setState({ jid: chatJid, list: [] });
      });
    return () => { cancelled = true; };
  }, [chatJid, organizationId, userId, enabled]);

  // Il risultato di un'altra chat (cambio chat veloce) non vale per questa
  const current = state && state.jid === chatJid ? state.list : null;
  return { candidates: current ?? [], loading: enabled && current === null };
}

/** Ricerca libera fra Lead e Clienti (nome o numero), con attesa fra un tasto e l'altro. */
export function useClientFreeSearch(organizationId: string, text: string) {
  const { userProfile } = useUserData();
  const userId = userProfile?.id ?? null;
  const [results, setResults] = useState<ClientChatCandidate[]>([]);
  const [searching, setSearching] = useState(false);
  const run = useRef(0);

  const search = useCallback((q: string) => {
    const id = ++run.current;
    if (q.trim().length < 2) { setResults([]); setSearching(false); return () => undefined; }
    setSearching(true);
    const timer = setTimeout(() => {
      searchClientsForChat(organizationId, userId, q.trim(), 20)
        .then((rows) => { if (id === run.current) setResults(rows); })
        .catch(() => { if (id === run.current) setResults([]); })
        .finally(() => { if (id === run.current) setSearching(false); });
    }, 250);
    return () => clearTimeout(timer);
  }, [organizationId, userId]);

  useEffect(() => search(text), [search, text]);
  return { results, searching };
}
